import { useCallback, useEffect, useRef } from 'react'
import {
  Location,
  NavigationType,
  useLocation,
  useNavigate,
  useNavigationType,
} from 'react-router'
import { useCompany } from '../useCompany'

export function usePushHistory() {
  const navigate = useNavigate()
  const company = useCompany()

  const pushHistory = useCallback(
    (href: string, evt?: { ctrlKey: boolean }, state?: unknown) => {
      const newTab = evt?.ctrlKey
      if (newTab) {
        const url = href.startsWith('/')
          ? `${window.location.origin}/c/${company.shortName}${href}`
          : `${window.location.href}/${href}`
        window.open(url)
      } else navigate(href, { state })
    },
    [company.shortName, navigate]
  )

  return pushHistory
}

/**
 * Runs `callback` whenever the URL changes.
 *
 * @param callback (location, navigationType) => void
 * @param enabled turn the listener on/off (defaults to `true`)
 *
 * @example
 * useLocationEffect((loc, type) => {
 *   if (type === "PUSH") closeDrawer();
 * }, isMobile);
 */
export function useLocationEffect(
  callback: (location: Location, navigationType: NavigationType) => void,
  enabled: boolean = true
) {
  const cbRef = useRef(callback)

  const location = useLocation()
  const navType = useNavigationType()

  useEffect(() => {
    cbRef.current = callback
  }, [callback])

  useEffect(() => {
    if (!enabled) return
    cbRef.current(location, navType)
  }, [enabled, location, navType])
}
